'use client';

import React, { useEffect, useRef, MouseEventHandler } from 'react';
import classNames from 'classnames';
import Image, { getImageProps } from 'next/image';
import Button from '@/components/Button/Button';
import WistiaVideoEmbed from '@/components/VideoCard/WistiaVideo/WistiaVideoEmbed';
import BrightcoveEmbed from '@/components/VideoCard/Brightcove/BrightcoveEmbed';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import { scrollLock, scrollUnlock, isScrollUnLocked } from '@/utils/scroll';
import { composeImageUrl } from '@/utils/imageAsset';
import { ContentGalleryType } from '@/utils/common.types';

interface Props {
  galleryItem: ContentGalleryType;
  onClose: MouseEventHandler<HTMLDivElement>;
}

const modalStack: React.MutableRefObject<HTMLInputElement | null>[] = [];

const GalleryModal = ({ galleryItem, onClose }: Props) => {
  const modalContainerRef = useRef<HTMLInputElement | null>(null);
  const checkLinkDomain = (slug: string) => {
    if (slug.startsWith('chbg/')) {
      return {
        href: `https://claytonhomebuildinggroup.com/${slug.split('chbg/')[1]}`,
        isOpenInNewWindow: true,
      };
    }
    if (slug.startsWith('cb/')) {
      return { href: slug.split('cb/')[1], isOpenInNewWindow: false };
    }

    return {
      href: slug,
      isOpenInNewWindow: true,
    };
  };

  useEffect(() => {
    if (isScrollUnLocked()) {
      scrollLock();
      return () => {
        scrollUnlock();
      };
    }
  }, []);

  // Handle exit using escape key via eventListener. If enter key is processed here for closing modal, we have problems
  // when enter key press is required for other things like a button (e.g. login modal button).
  const handleKeyDown = (e) => {
    if (e.keyCode === 27 && onClose) {
      if (modalStack[modalStack.length - 1] == modalContainerRef) {
        onClose(e);
      }
    }
  };

  // Handle exit using enter key via onKeyDown.
  const handleExit = (e) => {
    if (e.keyCode === 13 && onClose) {
      onClose(e);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    modalStack.push(modalContainerRef);

    if (modalContainerRef.current) {
      const focusableElements = modalContainerRef.current.querySelectorAll(
        'button, [href], div, [tabindex]:not([tabindex="-1"])'
      );
      (focusableElements[0] as HTMLElement)?.focus();
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      modalStack.splice(modalStack.indexOf(modalContainerRef), 1);
    };
  }, [handleKeyDown]);

  const imgCommon = {
    sizes: '50vw',
  };
  const {
    props: { srcSet: desktop, ...rest },
  } = getImageProps({
    ...imgCommon,
    width: 850,
    height: 450,
    quality: 80,
    src: composeImageUrl({
      image: galleryItem?.image?.desktop,
      w: 850,
      h: 450,
      fm: 'webp',
    }),
    alt: galleryItem?.image?.desktop?.title || 'Modal Image',
  });
  const {
    props: { srcSet: tablet },
  } = getImageProps({
    ...imgCommon,
    width: 600,
    height: 400,
    quality: 80,
    src: composeImageUrl({
      image: galleryItem?.image?.mobile,
      w: 600,
      h: 400,
      fm: 'webp',
    }),
    alt: galleryItem?.image?.mobile?.title || 'Modal Image',
  });
  const {
    props: { srcSet: mobile },
  } = getImageProps({
    ...imgCommon,
    width: 425,
    height: 300,
    quality: 70,
    src: composeImageUrl({
      image: galleryItem?.image?.mobile,
      w: 425,
      h: 300,
      fm: 'webp',
    }),
    alt: galleryItem?.image?.mobile?.title || 'Modal Image',
  });

  return (
    <div
      className="fixed left-0 top-0 z-[1000] flex h-full w-full items-center justify-center overflow-y-scroll bg-black-modal/85 blur-none"
      onClick={onClose}
    >
      <div
        className="mx-[5px] max-w-[400px] md:max-w-[600px] lg:max-w-[800px]"
        ref={modalContainerRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm:right=[10px] absolute right-[15px] top-[25px] z-[10] rounded-full bg-black-modal/85 text-end sm:top-[5px]">
          <span
            className="cursor-pointer"
            onClick={onClose}
            onKeyDown={handleExit}
          >
            <Image
              src="/public-assets/menu/x.svg"
              alt="Close Modal"
              height={35}
              width={35}
              className="mb-[-5px]"
            />
          </span>
        </div>
        <div className="relative mb-[1.5%]">
          {galleryItem?.__typename !== 'VideoCard' ? (
            <div className="modalImage">
              <picture className="object-cover md:h-[400px]">
                <source media="(min-width: 992px)" srcSet={desktop} />
                <source media="(min-width: 768px)" srcSet={tablet} />
                <source media="(min-width: 320px)" srcSet={mobile} />
                <Image
                  {...rest}
                  alt={galleryItem?.image?.desktop?.title || 'Modal Image'}
                  style={{ width: '100%', height: 'auto' }}
                />
              </picture>
            </div>
          ) : (
            galleryItem?.video && (
              <div className="w-full">
                {galleryItem.video?.host === 'wistia' ? (
                  <WistiaVideoEmbed videoId={galleryItem.video.id} />
                ) : (
                  <BrightcoveEmbed videoId={galleryItem.video.id} />
                )}
              </div>
            )
          )}
          <div
            className={classNames({
              ['mt-[-7px] bg-white px-5 py-2.5 md:w-full']:
                galleryItem?.title ||
                galleryItem?.description ||
                galleryItem?.button,
            })}
          >
            {galleryItem?.title && (
              <h3 className="my-2.5 font-acumin text-[22px] font-bold leading-[1.18] tracking-[-0.35px]">
                {galleryItem.title}
              </h3>
            )}
            {galleryItem?.description && (
              <div className="mb-5 font-source-sans text-[16px] leading-[-0.3px] tracking-[1.65]">
                <ContentfulRichText {...galleryItem?.description} />
              </div>
            )}
            {galleryItem?.button && (
              <div className="mb-2.5">
                <Button
                  label={galleryItem.button.label}
                  href={checkLinkDomain(galleryItem.button.url).href}
                  openNewWindow={
                    checkLinkDomain(galleryItem.button.url).isOpenInNewWindow
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryModal;
